/**
 * UpcomingDeliveriesPage - Redesigned with Quiz styling
 */

import React, { useMemo } from "react";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

import { fmtDate } from "../utils/format";
import { QuizStyledCard } from "../components/shared/QuizStyledCard";
import { PageTitle } from "../components/shared/PageTitle";
import { QuizButton } from "../components/shared/QuizButton";
import { extractLineItemsWithImages, groupLineItems } from "../utils/lineItems";
import { buildVariantIndex, estimateDeliveryDate } from "../utils/recharge";
import { QUIZ } from "@/theme/quizTokens";

// --------------------------------------
// Types (lightweight + tolerant)
// --------------------------------------
type ChargeLike = {
  id?: string | number;
  charge_id?: string | number;

  scheduled_at?: string;
  scheduled_for?: string;
  scheduled_on?: string;
  next_charge_scheduled_at?: string;
  date?: string;

  status?: string;
  state?: string;

  total_price?: string | number;
  total?: string | number;
  price?: string | number;

  currency?: string;

  order_number?: string | number;
  address_id?: string | number;

  // sometimes nested
  charge?: any;
  charges?: any;
};

// --------------------------------------
// Guards
// --------------------------------------
function isObject(x: any): x is Record<string, any> {
  return !!x && typeof x === "object" && !Array.isArray(x);
}

function isChargeObject(x: any): boolean {
  if (!isObject(x)) return false;
  return (
    "id" in x ||
    "charge_id" in x ||
    "scheduled_at" in x ||
    "next_charge_scheduled_at" in x ||
    "scheduled_for" in x ||
    "scheduled_on" in x
  );
}

// --------------------------------------
// Pickers
// --------------------------------------
export function pickScheduledAt(c: any): string | null {
  const src = c?.charge && isObject(c.charge) ? c.charge : c;
  return (
    src?.scheduled_at ||
    src?.scheduled_for ||
    src?.scheduled_on ||
    src?.next_charge_scheduled_at ||
    src?.date ||
    null
  );
}

function pickId(c: any): string {
  const src = c?.charge && isObject(c.charge) ? c.charge : c;
  const id =
    src?.id ??
    src?.charge_id ??
    src?.order_id ??
    src?.order_number ??
    src?.external_id;
  return String(id ?? "");
}

function pickStatus(c: any): string {
  const src = c?.charge && isObject(c.charge) ? c.charge : c;
  return String(src?.status ?? src?.state ?? "scheduled");
}

function pickTotal(c: any): string | null {
  const src = c?.charge && isObject(c.charge) ? c.charge : c;
  // Try multiple price fields in order of preference
  const v = src?.total_price ?? src?.subtotal_price ?? src?.total ?? src?.price ?? src?.subtotal;
  if (v == null || v === "") return null;
  const n = Number(v);
  if (Number.isFinite(n) && n > 0) return n.toFixed(2);
  // Try line items total as fallback
  if (Array.isArray(src?.line_items)) {
    const lineTotal = src.line_items.reduce((sum: number, item: any) => {
      const itemPrice = Number(item?.price || item?.total_price || 0);
      const qty = Number(item?.quantity || 1);
      return sum + (itemPrice * qty);
    }, 0);
    if (lineTotal > 0) return lineTotal.toFixed(2);
  }
  return String(v);
}

function pickCurrency(c: any): string {
  const src = c?.charge && isObject(c.charge) ? c.charge : c;
  return String(src?.currency || "GBP");
}

function pickAddressId(c: any): string | null {
  const src = c?.charge && isObject(c.charge) ? c.charge : c;
  const v = src?.address_id ?? src?.address?.id;
  if (v == null || v === "") return null;
  return String(v);
}

// --------------------------------------
// Normalizer (accept messy shapes + single object)
// --------------------------------------
export function normalizeUpcomingCharges(upcoming: any): ChargeLike[] {
  if (!upcoming) return [];

  // 1) direct array
  if (Array.isArray(upcoming))
    return sortByDateAsc(upcoming.filter(isChargeObject));

  // 2) common wrapper shapes
  const candidates = [
    upcoming?.charges,
    upcoming?.data?.charges,
    upcoming?.charges?.data,
    upcoming?.upcoming?.charges,
    upcoming?.upcoming_charges, // common
    upcoming?.data?.upcoming_charges, // common
    upcoming?.upcoming, // sometimes already a charge-like object
    upcoming?.data, // sometimes array
  ];

  for (const c of candidates) {
    if (Array.isArray(c)) return sortByDateAsc(c.filter(isChargeObject));
  }

  // 3) sometimes `upcoming` contains a single charge object
  if (isChargeObject(upcoming)) return [upcoming];

  // 4) sometimes `{ charges: { ...singleCharge } }`
  if (isChargeObject(upcoming?.charges)) return [upcoming.charges];

  return [];
}

function sortByDateAsc(list: ChargeLike[]) {
  const out = [...list];
  out.sort((a, b) => {
    const da = new Date(pickScheduledAt(a) || 0).getTime();
    const db = new Date(pickScheduledAt(b) || 0).getTime();
    return (Number.isFinite(da) ? da : 0) - (Number.isFinite(db) ? db : 0);
  });
  return out;
}

// --------------------------------------
// UI
// --------------------------------------
function statusChip(status: string) {
  const raw = String(status || "scheduled").trim();
  const s = raw.toLowerCase();

  const label =
    s === "queued"
      ? "Queued"
      : s === "success"
      ? "Processed"
      : s === "skipped"
      ? "Skipped"
      : s === "cancelled"
      ? "Cancelled"
      : s === "error"
      ? "Error"
      : s === "scheduled"
      ? "Scheduled"
      : raw;

  // Quiz-style colors
  const bgColor =
    s === "success"
      ? "#E5FFEA"
      : s === "error"
      ? "#FFE5E2"
      : s === "queued"
      ? "#E6F0FF"
      : s === "skipped" || s === "cancelled"
      ? QUIZ.color.cream2
      : "#FFF0D6";

  const textColor =
    s === "success"
      ? "#2E7D32"
      : s === "error"
      ? "#D14C3F"
      : s === "queued"
      ? "#1A57D3"
      : s === "skipped" || s === "cancelled"
      ? "#666"
      : "#CE7A00";

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        background: bgColor,
        color: textColor,
        fontWeight: 700,
        textTransform: "capitalize",
        height: 28,
        "& .MuiChip-label": { px: 1.5, fontSize: 12 },
      }}
    />
  );
}

function money(currency: string, amount?: string | null) {
  if (!amount) return "—";
  if (currency === "GBP") return `£${amount}`;
  return `${amount} ${currency}`;
}

// --------------------------------------
// Page
// --------------------------------------
/**
 * Get delivery date for a charge derived from charge date.
 * Formula: charge date + cutoff hours → snap to next available delivery day.
 */
function getDeliveryDateForCharge(charge: any, _addresses: any[], cutoffHours = 36, deliveryDayNames: string[] = []): string | null {
  const scheduledAt = pickScheduledAt(charge);
  if (scheduledAt) {
    return estimateDeliveryDate(scheduledAt, cutoffHours, deliveryDayNames);
  }
  return null;
}

export function UpcomingDeliveriesPage({
  upcoming,
  addresses = [],
  products = [],
  subscriptions = [],
  pendingOnetimes = [],
  cutoffHours = 36,
  deliveryDayNames = [],
  onEditMeals,
}: {
  upcoming: any;
  addresses?: any[];
  products?: any[];
  subscriptions?: any[];
  pendingOnetimes?: any[];
  cutoffHours?: number;
  deliveryDayNames?: string[];
  onEditMeals?: (charge: any) => void;
}) {
  const charges = useMemo(() => normalizeUpcomingCharges(upcoming), [upcoming]);

  // Build variant index for pending onetimes (same utility as ExtrasPage)
  const variantIndex = useMemo(() => buildVariantIndex(products || []), [products]);

  // Format pending onetimes with images
  const formattedPendingOnetimes = useMemo(() => {
    return pendingOnetimes.map((item) => {
      const variantId = String(
        item.shopify_variant_id || item.external_variant_id?.ecommerce || ""
      );
      const info = variantIndex.get(variantId);
      return {
        id: item.id,
        variantId,
        title: info?.productTitle || item.product_title || "Product",
        variantTitle: info?.variantTitle || item.variant_title || "",
        quantity: item.quantity || 1,
        price: item.price || "0",
        image: info?.imageUrl || null,
      };
    });
  }, [pendingOnetimes, variantIndex]);

  return (
    <Stack spacing={{ xs: 3, md: 4 }}>
      <QuizStyledCard>
        <Box sx={{ mb: 3 }}>
          <PageTitle
            title="Upcoming Deliveries"
            subtitle="View your scheduled deliveries"
          />
        </Box>

        {charges.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 4, md: 6 },
            }}
          >
            <Box
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                borderRadius: "50%",
                backgroundColor: QUIZ.color.cream2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <LocalShippingIcon
                sx={{ fontSize: { xs: 40, md: 50 }, color: QUIZ.color.brown }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 22 },
                fontWeight: 800,
                color: QUIZ.color.brown,
                mb: 1,
              }}
            >
              No upcoming deliveries
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                color: QUIZ.color.brown,
                opacity: 0.75,
              }}
            >
              Your next delivery will appear here once scheduled
            </Typography>
          </Box>
        ) : (
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            {charges.map((c, idx) => {
              const id = pickId(c) || `charge-${idx}`;
              const scheduledAt = pickScheduledAt(c);
              const deliveryDate = getDeliveryDateForCharge(c, addresses, cutoffHours, deliveryDayNames);
              const status = pickStatus(c);
              const total = pickTotal(c);
              const currency = pickCurrency(c);
              const addressId = pickAddressId(c);

              return (
                <QuizStyledCard variant="white" key={id}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 18, md: 20 },
                          fontWeight: 800,
                          color: QUIZ.color.brown,
                          mb: 0.5,
                        }}
                      >
                        Delivery {deliveryDate ? fmtDate(deliveryDate) : "—"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: 13, md: 14 },
                          color: QUIZ.color.brown,
                          opacity: 0.65,
                        }}
                      >
                        Total: {money(currency, total)}
                      </Typography>
                    </Box>

                    {statusChip(status)}
                  </Box>

                  {(() => {
                    const lineItems = extractLineItemsWithImages(c?.charge || c, products, subscriptions);
                    if (lineItems.length === 0) return null;

                    const grouped = groupLineItems(lineItems);
                    const hasBundles = grouped.bundles.length > 0;
                    const hasMeals = grouped.meals.length > 0;
                    const hasTreats = grouped.treats.length > 0;
                    const hasOther = grouped.other.length > 0;

                    if (!hasBundles && !hasMeals && !hasTreats && !hasOther) return null;

                    return (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Stack spacing={2.5}>
                          {/* Bundle Products with nested meals */}
                          {hasBundles && (
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: { xs: 14, md: 15 },
                                  fontWeight: 800,
                                  color: QUIZ.color.brown,
                                  mb: 1.5,
                                }}
                              >
                                Meal Plans
                              </Typography>
                              <Stack spacing={2}>
                                {grouped.bundles.map((bundle) => (
                                  <Box
                                    key={bundle.variantId}
                                    sx={{
                                      border: "1px solid rgba(0,0,0,.08)",
                                      borderRadius: { xs: "12px", md: "14px" },
                                      overflow: "hidden",
                                      background: QUIZ.color.cream,
                                    }}
                                  >
                                    {/* Bundle Header */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1.5,
                                        alignItems: "center",
                                        p: { xs: 1.25, md: 1.5 },
                                        background: QUIZ.color.cream,
                                        borderBottom: bundle.subItems && bundle.subItems.length > 0
                                          ? "1px solid rgba(0,0,0,.08)"
                                          : "none",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: { xs: 48, md: 56 },
                                          height: { xs: 48, md: 56 },
                                          borderRadius: "10px",
                                          overflow: "hidden",
                                          border: "1px solid rgba(0,0,0,.10)",
                                          background: QUIZ.color.warmWhite,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {bundle.imageUrl ? (
                                          <Box
                                            component="img"
                                            src={bundle.imageUrl}
                                            alt={bundle.productTitle}
                                            sx={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                              display: "block",
                                            }}
                                          />
                                        ) : null}
                                      </Box>
                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                          sx={{
                                            fontWeight: 800,
                                            fontSize: { xs: 14, md: 15 },
                                            color: QUIZ.color.brown,
                                          }}
                                          noWrap
                                        >
                                          {bundle.productTitle}
                                        </Typography>
                                        {bundle.variantTitle && (
                                          <Typography
                                            sx={{
                                              opacity: 0.65,
                                              fontSize: { xs: 11, md: 12 },
                                              color: QUIZ.color.brown,
                                              mt: 0.25,
                                            }}
                                            noWrap
                                          >
                                            {bundle.variantTitle}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Chip
                                        label={`×${bundle.quantity}`}
                                        size="small"
                                        sx={{
                                          background: "#E5FFEA",
                                          color: "#2E7D32",
                                          fontWeight: 800,
                                          height: 28,
                                          "& .MuiChip-label": { px: 1.5, fontSize: 13 },
                                        }}
                                      />
                                    </Box>

                                    {/* Nested Meals */}
                                    {bundle.subItems && bundle.subItems.length > 0 && (
                                      <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                                        <Typography
                                          sx={{
                                            fontSize: { xs: 11, md: 12 },
                                            fontWeight: 700,
                                            color: QUIZ.color.brown,
                                            opacity: 0.5,
                                            mb: 1,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                          }}
                                        >
                                          Meals in this plan
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                              xs: "repeat(2, 1fr)",
                                              sm: "repeat(3, 1fr)",
                                              md: "repeat(4, 1fr)",
                                            },
                                            gap: 1,
                                          }}
                                        >
                                          {bundle.subItems.map((meal) => (
                                            <Box
                                              key={meal.variantId}
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                p: 1,
                                                borderRadius: "8px",
                                                background: QUIZ.color.warmWhite,
                                                border: "1px solid rgba(0,0,0,.06)",
                                              }}
                                            >
                                              {meal.imageUrl && (
                                                <Box
                                                  component="img"
                                                  src={meal.imageUrl}
                                                  alt={meal.productTitle}
                                                  sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: "6px",
                                                    objectFit: "cover",
                                                    flexShrink: 0,
                                                  }}
                                                />
                                              )}
                                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography
                                                  sx={{
                                                    fontSize: { xs: 11, md: 12 },
                                                    fontWeight: 700,
                                                    color: QUIZ.color.brown,
                                                    lineHeight: 1.2,
                                                  }}
                                                  noWrap
                                                >
                                                  {meal.productTitle}
                                                </Typography>
                                                <Typography
                                                  sx={{
                                                    fontSize: { xs: 10, md: 11 },
                                                    color: "#2E7D32",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  ×{meal.quantity}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {/* Individual Meals (non-bundle) */}
                          {hasMeals && (
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: { xs: 14, md: 15 },
                                  fontWeight: 800,
                                  color: QUIZ.color.brown,
                                  mb: 1.5,
                                }}
                              >
                                Meals & Recipes
                              </Typography>
                              <Stack spacing={1.5}>
                                {grouped.meals.map((item) => (
                                  <Box
                                    key={item.variantId}
                                    sx={{
                                      display: "flex",
                                      gap: 1.5,
                                      alignItems: "center",
                                      border: "1px solid rgba(0,0,0,.08)",
                                      borderRadius: { xs: "10px", md: "12px" },
                                      p: { xs: 1, md: 1.25 },
                                      background: QUIZ.color.cream,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: { xs: 48, md: 56 },
                                        height: { xs: 48, md: 56 },
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                        border: "1px solid rgba(0,0,0,.10)",
                                        background: QUIZ.color.cream,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {item.imageUrl ? (
                                        <Box
                                          component="img"
                                          src={item.imageUrl}
                                          alt={item.productTitle}
                                          sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                          }}
                                        />
                                      ) : null}
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 800,
                                          fontSize: { xs: 13, md: 14 },
                                          color: QUIZ.color.brown,
                                        }}
                                        noWrap
                                      >
                                        {item.productTitle}
                                      </Typography>
                                      {item.variantTitle && (
                                        <Typography
                                          sx={{
                                            opacity: 0.65,
                                            fontSize: { xs: 11, md: 12 },
                                            color: QUIZ.color.brown,
                                            mt: 0.25,
                                          }}
                                          noWrap
                                        >
                                          {item.variantTitle}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Chip
                                      label={`×${item.quantity}`}
                                      size="small"
                                      sx={{
                                        background: "#E5FFEA",
                                        color: "#2E7D32",
                                        fontWeight: 800,
                                        height: 28,
                                        "& .MuiChip-label": { px: 1.5, fontSize: 13 },
                                      }}
                                    />
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {/* Treats & Add-ons */}
                          {hasTreats && (
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: { xs: 14, md: 15 },
                                  fontWeight: 800,
                                  color: "#CE7A00",
                                  mb: 1.5,
                                }}
                              >
                                Treats & Add-ons
                              </Typography>
                              <Stack spacing={1.5}>
                                {grouped.treats.map((item) => (
                                  <Box
                                    key={item.variantId}
                                    sx={{
                                      display: "flex",
                                      gap: 1.5,
                                      alignItems: "center",
                                      border: "1px solid rgba(0,0,0,.08)",
                                      borderRadius: { xs: "10px", md: "12px" },
                                      p: { xs: 1, md: 1.25 },
                                      background: QUIZ.color.cream,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: { xs: 48, md: 56 },
                                        height: { xs: 48, md: 56 },
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                        border: "1px solid rgba(0,0,0,.10)",
                                        background: QUIZ.color.cream,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {item.imageUrl ? (
                                        <Box
                                          component="img"
                                          src={item.imageUrl}
                                          alt={item.productTitle}
                                          sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                          }}
                                        />
                                      ) : null}
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 800,
                                          fontSize: { xs: 13, md: 14 },
                                          color: QUIZ.color.brown,
                                        }}
                                        noWrap
                                      >
                                        {item.productTitle}
                                      </Typography>
                                      {item.variantTitle && (
                                        <Typography
                                          sx={{
                                            opacity: 0.65,
                                            fontSize: { xs: 11, md: 12 },
                                            color: QUIZ.color.brown,
                                            mt: 0.25,
                                          }}
                                          noWrap
                                        >
                                          {item.variantTitle}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Chip
                                      label={`×${item.quantity}`}
                                      size="small"
                                      sx={{
                                        background: "#E5FFEA",
                                        color: "#2E7D32",
                                        fontWeight: 800,
                                        height: 28,
                                        "& .MuiChip-label": { px: 1.5, fontSize: 13 },
                                      }}
                                    />
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {/* Next Delivery Add-ons (pending one-times) - only show on first charge */}
                          {idx === 0 && formattedPendingOnetimes.length > 0 && (
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: { xs: 14, md: 15 },
                                  fontWeight: 800,
                                  color: QUIZ.color.brown,
                                  mb: 1.5,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                Next Delivery Add-ons
                                <Chip
                                  label="Next delivery only"
                                  size="small"
                                  sx={{
                                    background: "#FFE8A3",
                                    color: QUIZ.color.brown,
                                    fontWeight: 700,
                                    height: 22,
                                    fontSize: 10,
                                  }}
                                />
                              </Typography>
                              <Stack spacing={1.5}>
                                {formattedPendingOnetimes.map((item) => (
                                  <Box
                                    key={item.id}
                                    sx={{
                                      display: "flex",
                                      gap: 1.5,
                                      alignItems: "center",
                                      border: "1px solid rgba(74, 48, 38, 0.3)",
                                      borderRadius: { xs: "10px", md: "12px" },
                                      p: { xs: 1, md: 1.25 },
                                      background: "linear-gradient(135deg, #FFF9F0 0%, #FFF5E8 100%)",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: { xs: 48, md: 56 },
                                        height: { xs: 48, md: 56 },
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                        border: "1px solid rgba(0,0,0,.10)",
                                        background: QUIZ.color.cream,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {item.image ? (
                                        <Box
                                          component="img"
                                          src={item.image}
                                          alt={item.title}
                                          sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                          }}
                                        />
                                      ) : null}
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 800,
                                          fontSize: { xs: 13, md: 14 },
                                          color: QUIZ.color.brown,
                                        }}
                                        noWrap
                                      >
                                        {item.title}
                                      </Typography>
                                      {item.variantTitle && (
                                        <Typography
                                          sx={{
                                            opacity: 0.65,
                                            fontSize: { xs: 11, md: 12 },
                                            color: QUIZ.color.brown,
                                            mt: 0.25,
                                          }}
                                          noWrap
                                        >
                                          {item.variantTitle}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Chip
                                      label={`×${item.quantity}`}
                                      size="small"
                                      sx={{
                                        background: "#FFE8A3",
                                        color: QUIZ.color.brown,
                                        fontWeight: 800,
                                        height: 28,
                                        "& .MuiChip-label": { px: 1.5, fontSize: 13 },
                                      }}
                                    />
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {/* Other Products */}
                          {hasOther && (
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: { xs: 14, md: 15 },
                                  fontWeight: 800,
                                  color: QUIZ.color.brown,
                                  mb: 1.5,
                                }}
                              >
                                Other Products
                              </Typography>
                              <Stack spacing={1.5}>
                                {grouped.other.map((item) => (
                                  <Box
                                    key={item.variantId}
                                    sx={{
                                      display: "flex",
                                      gap: 1.5,
                                      alignItems: "center",
                                      border: "1px solid rgba(0,0,0,.08)",
                                      borderRadius: { xs: "10px", md: "12px" },
                                      p: { xs: 1, md: 1.25 },
                                      background: QUIZ.color.cream,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: { xs: 48, md: 56 },
                                        height: { xs: 48, md: 56 },
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                        border: "1px solid rgba(0,0,0,.10)",
                                        background: QUIZ.color.cream,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {item.imageUrl ? (
                                        <Box
                                          component="img"
                                          src={item.imageUrl}
                                          alt={item.productTitle}
                                          sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                          }}
                                        />
                                      ) : null}
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 800,
                                          fontSize: { xs: 13, md: 14 },
                                          color: QUIZ.color.brown,
                                        }}
                                        noWrap
                                      >
                                        {item.productTitle}
                                      </Typography>
                                      {item.variantTitle && (
                                        <Typography
                                          sx={{
                                            opacity: 0.65,
                                            fontSize: { xs: 11, md: 12 },
                                            color: QUIZ.color.brown,
                                            mt: 0.25,
                                          }}
                                          noWrap
                                        >
                                          {item.variantTitle}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Chip
                                      label={`×${item.quantity}`}
                                      size="small"
                                      sx={{
                                        background: "#E5FFEA",
                                        color: "#2E7D32",
                                        fontWeight: 800,
                                        height: 28,
                                        "& .MuiChip-label": { px: 1.5, fontSize: 13 },
                                      }}
                                    />
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </>
                    );
                  })()}

                  {onEditMeals && (status === "scheduled" || status === "queued") && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <QuizButton
                          variant="primary"
                          onClick={() => onEditMeals(c)}
                          fullWidth
                          sx={{ flex: 1 }}
                        >
                          Edit Meals
                        </QuizButton>
                      </Stack>
                    </>
                  )}
                </QuizStyledCard>
              );
            })}
          </Stack>
        )}
      </QuizStyledCard>
    </Stack>
  );
}
